import { Pipe, PipeTransform } from '@angular/core';
import { Currencies } from '@app-shared/store/currencies/currencies-facade.service';
import { map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { Locales, UserInfoProfileService } from '@app/shared/services/user-info-profile.service';
import { formatNumber } from '@angular/common';
import BigNumber from 'bignumber.js';
import { CurrencyDto } from '@app/generated/models/currency-dto';

@Pipe({
  name: 'appCurrencyAmount',
  standalone: true,
})
export class AppCurrencyAmountPipe implements PipeTransform {
  private unsubscribe$ = new Subject<void>();

  constructor(
    private readonly userInfoProfileService: UserInfoProfileService,
    private readonly currenciesFacade: Currencies,
  ) {}

  transformNumber(
    amount: number,
    numberLocale: string = Locales.cs,
    currencyName: CurrencyDto['name'],
    currencies: CurrencyDto[] = [],
    removeTrailingZeros: boolean = true,
  ) {
    const amountBN = new BigNumber(amount);
    const currency = currencies.find((currency) => currency.name === currencyName);
    const decimalPlaces = currency?.guiDecimalCount || 8;
    const digitsInfo = ['.', removeTrailingZeros ? 0 : decimalPlaces, '-', decimalPlaces].join('');
    const flooredAmountBN = amountBN.decimalPlaces(decimalPlaces, BigNumber.ROUND_DOWN);

    return formatNumber(flooredAmountBN.toNumber(), numberLocale, digitsInfo);
  }

  transform(
    amount$: Observable<number> | number,
    currencyName: CurrencyDto['name'],
    removeTrailingZeros: boolean = true,
  ): Observable<string> {
    if (typeof amount$ === 'number') {
      amount$ = of(amount$);
    }

    return combineLatest([amount$, this.userInfoProfileService.numberLocale$]).pipe(
      withLatestFrom(this.currenciesFacade.currencies$),
      takeUntil(this.unsubscribe$),
      map(([[amount, numberLocale], currencies]: [[number, string], CurrencyDto[]]) => {
        return this.transformNumber(amount, numberLocale, currencyName, currencies, removeTrailingZeros);
      }),
    );
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
